<template>
  <button
    type="button"
    class="locale-switcher"
    :class="{ 'locale-switcher--en': current === 'en' }"
    role="switch"
    :aria-checked="current === 'en'"
    :aria-label="$t('locale.switchAria')"
    :title="$t('locale.toggleTitle')"
    @click="toggleLocale"
  >
    <span class="locale-switcher__track" aria-hidden="true">
      <span class="locale-switcher__thumb" />
      <span class="locale-switcher__labels">
        <span class="locale-switcher__label">{{ $t('locale.esCL') }}</span>
        <span class="locale-switcher__label">{{ $t('locale.en') }}</span>
      </span>
    </span>
  </button>
</template>

<script>
import router from '@/router';
import { setStoredLocale } from '@/i18n';
import { buildPrimeVueLocaleEsCL } from '@/utils/primevueLocaleEsCL';
import { applyRouteMeta } from '@/utils/seo';

export default {
  name: 'LocaleSwitcher',
  computed: {
    current() {
      return this.$i18n.locale;
    },
  },
  methods: {
    toggleLocale() {
      const next = this.current === 'es-CL' ? 'en' : 'es-CL';
      this.setLocale(next);
    },
    setLocale(code) {
      if (this.$i18n.locale === code) return;
      this.$i18n.locale = code;
      setStoredLocale(code);
      try {
        const pv = this.$primevue;
        if (pv && pv.config) {
          pv.config.locale = buildPrimeVueLocaleEsCL();
        }
      } catch {
        /* noop */
      }
      applyRouteMeta(router.currentRoute.value);
    },
  },
};
</script>

<style scoped>
.locale-switcher {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.35rem;
  padding: 0.18rem;
  border: 1px solid rgba(201, 169, 98, 0.35);
  border-radius: 999px;
  background: var(--locale-switcher-btn-bg, rgba(0, 0, 0, 0.2));
  color: var(--text-color);
  cursor: pointer;
  line-height: 1;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.locale-switcher:hover {
  border-color: rgba(201, 169, 98, 0.55);
  color: var(--primary-color);
}

.locale-switcher:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.locale-switcher__track {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  min-width: 4.15rem;
  padding: 0.2rem 0.15rem;
}

.locale-switcher__thumb {
  position: absolute;
  top: 0.1rem;
  bottom: 0.1rem;
  left: 0.1rem;
  width: calc(50% - 0.2rem);
  border-radius: 999px;
  background: rgba(201, 169, 98, 0.28);
  border: 1px solid rgba(201, 169, 98, 0.45);
  transition:
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.2s ease;
  z-index: 0;
  pointer-events: none;
}

.locale-switcher--en .locale-switcher__thumb {
  transform: translateX(calc(100% + 0.2rem));
}

.locale-switcher__labels {
  grid-column: 1 / -1;
  grid-row: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  z-index: 1;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  pointer-events: none;
}

.locale-switcher__label {
  text-align: center;
  padding: 0.12rem 0.35rem;
  color: var(--text-muted-600);
  transition: color 0.15s ease;
}

.locale-switcher--en .locale-switcher__label:nth-child(2),
.locale-switcher:not(.locale-switcher--en) .locale-switcher__label:nth-child(1) {
  color: var(--text-color);
  font-weight: 800;
}
</style>
